import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Envelope, Eye, PencilSimple, FileText } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  "Appels d'offres": ['invitation_dce', 'offre_dce_soumise', 'relance_dce', 'lot_attribue', 'lot_non_retenu'],
  'Suivi chantier': ['convocation_reunion', 'rappel_reunion', 'nouveau_crc', 'nouvelle_reserve', 'reserve_levee'],
  'Actions': ['nouvelle_tache', 'rappel_tache'],
  'Documents': ['nouveau_document'],
  'Messages': ['nouveau_message'],
  'Finances': ['nouveau_devis', 'relance_devis', 'nouvelle_situation', 'situation_validee'],
  'Chantier': ['invitation_projet'],
  'Système': ['bienvenue', 'reset_password'],
};

const LABELS = {
  'invitation_dce': "Invitation à répondre à un appel d'offres",
  'offre_dce_soumise': 'Offre envoyée par une entreprise',
  'relance_dce': "Relance — appel d'offres en attente de réponse",
  'lot_attribue': 'Lot attribué (entreprise retenue)',
  'lot_non_retenu': 'Lot non retenu (entreprise écartée)',
  'convocation_reunion': 'Convocation à une réunion de chantier',
  'rappel_reunion': 'Rappel de réunion (la veille)',
  'nouveau_crc': 'Nouveau compte rendu de chantier',
  'nouvelle_reserve': 'Nouvelle réserve à traiter',
  'reserve_levee': 'Réserve levée',
  'nouvelle_tache': 'Nouvelle action assignée',
  'rappel_tache': 'Rappel : action à échéance proche',
  'nouveau_document': 'Nouveau document partagé',
  'nouveau_message': 'Nouveau message',
  'nouveau_devis': 'Nouveau devis reçu',
  'relance_devis': 'Rappel : devis en attente',
  'nouvelle_situation': 'Nouvelle situation de travaux',
  'situation_validee': 'Situation de travaux validée',
  'invitation_projet': 'Invitation à rejoindre un chantier',
  'bienvenue': 'Bienvenue (nouveau compte)',
  'reset_password': 'Réinitialisation mot de passe',
};

const CATEGORY_BADGES = {
  "Appels d'offres": 'bg-[#FFF3E0] text-[#C75B39]',
  'Suivi chantier': 'bg-[#E8F5E9] text-[#2D6A4F]',
  'Actions': 'bg-[#F3E5F5] text-[#6A1B9A]',
  'Documents': 'bg-[#E3F2FD] text-[#1E3A5F]',
  'Messages': 'bg-[#E0F7FA] text-[#00838F]',
  'Finances': 'bg-[#FFF8E1] text-[#B8860B]',
  'Chantier': 'bg-[#FFEBEE] text-[#8B3A3A]',
  'Système': 'bg-[#F5F5F3] text-[#4A4A4A]',
};

export default function EmailTemplatesList({ onSelectTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/email-templates`, { withCredentials: true });
      setTemplates(data);
    } catch (e) {
      toast.error("Erreur de chargement des modèles d'e-mails");
    } finally {
      setLoading(false);
    }
  };

  const templatesByKey = templates.reduce((acc, t) => {
    acc[t.template_key] = t;
    return acc;
  }, {});

  const customCount = templates.filter(t => t.is_custom).length;

  if (loading) {
    return <div className="p-8 text-center text-sm text-[#6B6B6B]">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="email-templates-list">
      <div className="border-l-4 border-[#1E3A5F] bg-[#F0F4F8] p-4">
        <div className="flex items-start gap-3">
          <FileText size={20} className="text-[#1E3A5F] mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-[#2C2C2C] mb-1">Modèles d'e-mails aux couleurs du cabinet</p>
            <p className="text-xs text-[#6B6B6B] leading-relaxed">
              Ces {templates.length} modèles sont envoyés automatiquement avec l'identité du cabinet
              (logo, couleurs, signature). Personnalisez le sujet et le contenu HTML de chacun via l'éditeur intégré.
              {customCount > 0 && (
                <span className="text-[#C75B39] font-bold"> {customCount} modèle{customCount > 1 ? 's' : ''} personnalisé{customCount > 1 ? 's' : ''}.</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {Object.entries(CATEGORIES).map(([category, keys]) => (
        <div key={category}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#2C2C2C]">{category}</h3>
            <span className="text-xs text-[#9A9A9A]">{keys.length} modèle{keys.length > 1 ? 's' : ''}</span>
          </div>
          <div className="border border-[#E0E0DB] bg-white divide-y divide-[#E0E0DB]">
            {keys.map(key => {
              const t = templatesByKey[key];
              if (!t) return null;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between px-4 py-3 hover:bg-[#FAFAF8] transition-colors"
                  data-testid={`template-row-${key}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Envelope size={18} className="text-[#1E3A5F] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#2C2C2C] truncate">{LABELS[key] || key}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {t.is_custom ? (
                          <span className={`text-xs px-2 py-0.5 ${CATEGORY_BADGES[category] || 'bg-orange-100 text-orange-800'} font-bold`}>
                            Personnalisé v{t.version}
                          </span>
                        ) : (
                          <span className="text-xs bg-[#F5F5F3] text-[#6B6B6B] px-2 py-0.5">
                            Par défaut
                          </span>
                        )}
                        {t.updated_at && (
                          <span className="text-xs text-[#9A9A9A]">
                            modifié le {new Date(t.updated_at).toLocaleDateString('fr-FR')}
                            {t.updated_by_user_name ? ` par ${t.updated_by_user_name}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => onSelectTemplate(key, 'preview')}
                      className="text-xs flex items-center gap-1 px-3 py-1.5 border border-[#E0E0DB] hover:bg-[#F5F5F3] transition-colors"
                      title="Aperçu"
                      data-testid={`btn-preview-${key}`}
                    >
                      <Eye size={14} /> Aperçu
                    </button>
                    <button
                      onClick={() => onSelectTemplate(key, 'edit')}
                      className="text-xs flex items-center gap-1 px-3 py-1.5 bg-[#1E3A5F] text-white border border-[#1E3A5F] hover:bg-[#2C4A6F] transition-colors font-bold"
                      data-testid={`btn-edit-${key}`}
                    >
                      <PencilSimple size={14} /> Éditer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
